import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IonicAuth } from '@ionic-enterprise/auth';
import { AuthResult } from '@ionic-enterprise/auth/dist/esm/interfaces';
import { Platform } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { switchMap, take } from 'rxjs/operators';
import { LogService } from '~gc/shared/services/log.service';
import { ConfigService } from '../../../shared/services/config.service';
import { AppState } from '../../state';
import { authenticatedRole } from './auth.selectors';

const TAGS = ['Service', 'Auth', 'AuthConnect'];

@Injectable({ providedIn: 'root' })
export class AuthConnectService extends IonicAuth {
  constructor(
    private readonly platform: Platform,
    private readonly router: Router,
    private readonly log: LogService,
    private readonly store: Store<AppState>,
    private readonly config: ConfigService,
  ) {
    super(config.authConfig);
  }

  override onLoginSuccess(result: AuthResult): void {
    this.log.debug(TAGS, 'Login successful, redirecting...');

    // TODO: The following code does not have a subscription, so probably never gets executed!!!
    this.store.select(authenticatedRole).pipe(
      take(1),
      switchMap(role =>
        !!role ? this.router.navigateByUrl('/app/dashboard') : this.router.navigateByUrl('/login-landing'),
      ),
    );
  }

  override async onLogout(): Promise<void> {
    this.log.debug(TAGS, 'Logout completed.');
    await this.clearStorage();
    await this.router.navigateByUrl('/login');
  }

  getJWT(): Promise<string | undefined> {
    return super.getImplementation(this.config.authConfig).getRawIdToken();
  }

  /**
   * Redirect-based Silent Auth: navigate to Auth0 with prompt=none so that an existing
   * SSO session (e.g. from another app on same Auth0 tenant) can log the user in without
   * showing the login UI. Only used on web; iframe silent auth often fails due to
   * third-party cookie blocking. Returns without redirecting if not web or PKCE.
   */
  async trySilentAuthRedirect(): Promise<void> {
    const impl = (this as unknown as { implementation: { options: { platform?: string; webAuthFlow?: string }; authConfig: { generateChallengeAndVerifier: () => Promise<{ challenge: string; verifier: string }>; getAuthorizeUrl: (nonce: string, challenge: string, params: Record<string, unknown>) => Promise<{ url: string }> }; session: { clearAuthData: () => Promise<void>; setAuthData: (data: unknown) => Promise<void>; setNonce: (nonce: string) => Promise<void> }; getOverrideDiscoveryUrl: () => Promise<string | undefined> } }).implementation;
    if (!impl?.options || impl.options.platform !== 'web' || impl.options.webAuthFlow === 'PKCE') {
      return;
    }
    await impl.getOverrideDiscoveryUrl();
    const keys = await impl.authConfig.generateChallengeAndVerifier();
    const nonce = this.randomNonce();
    await impl.session.clearAuthData();
    await impl.session.setAuthData(keys);
    await impl.session.setNonce(nonce);
    const urlInfo = await impl.authConfig.getAuthorizeUrl(nonce, keys.challenge, {});
    const sep = urlInfo.url.includes('?') ? '&' : '?';
    const url = `${urlInfo.url}${sep}prompt=none`;
    this.log.debug(TAGS, 'Silent auth redirect to Auth0 (prompt=none).');
    window.location.replace(url);
  }

  private randomNonce(): string {
    const bytes = new Uint8Array(32);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(bytes);
    } else {
      for (let i = 0; i < 32; i++) {
        bytes[i] = Math.floor(Math.random() * 256);
      }
    }
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
